import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type {
  ActionDashboardItem,
  ResolutionConfigItemType,
} from '@frontend/api';
import {
  Button,
  PartThumbnail,
  HStack,
  Heading,
  Text,
  Part,
  PartThumbnailContainer,
  VStack,
  Flex,
} from '@frontend/components';
import { useDateFormat } from '@frontend/utils';

import { InteractiveFlex } from 'components/lib2/InteractiveContainers';

import useGetActiveUserGroupMembers from 'modules/common/userGroupManagement/switchUserGroup/hooks/useGetActiveUserGroupMembers';
import { buildPopOutViewerUrl } from 'modules/quotes/utils/popOutViewer';
import {
  getResolutionIcon,
  getIsDestructive,
} from 'modules/rules/utils/resolutions';

import { getGroupPrimaryKey } from 'store/session/selectors';

import { pluralize } from 'utils/stringUtils';

import ActionBusinessNameBadge from './ActionBusinessNameBadge';
import ActionSwitchGroupPopover from './ActionSwitchGroupPopover';

const ResolutionIcon = ({
  resolutionType,
}: {
  resolutionType: ResolutionConfigItemType;
}) => {
  const Icon = getResolutionIcon(resolutionType);
  const isDestructive = getIsDestructive(resolutionType);
  return (
    <Icon
      boxSize={2}
      color={isDestructive ? 'interaction.destructive' : 'feature.placeholder'}
    />
  );
};

type ReqsReviewActionProps = {
  item: ActionDashboardItem;
};

export const ReqsReviewAction = ({ item }: ReqsReviewActionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isQuoteLinkPopoverOpen, setIsQuoteLinkPopoverOpen] = useState(false);

  const groupId = useSelector(getGroupPrimaryKey);
  const { data: userGroupMembers } = useGetActiveUserGroupMembers();

  const isAssignedByUser = !item.assignedAutomatically;
  const assignedByName = item.assignedBy
    ? `${item.assignedBy.firstName} ${item.assignedBy.lastName}`
    : null;

  const { quoteLink, viewerLink } = useMemo(() => {
    const baseQuoteLink = `/quotes/edit/${item.component.quoteNumberWithRevision}/${item.component.quoteItemId}`;
    return {
      quoteLink: item.component.isRootComponent
        ? `${baseQuoteLink}?app=reqs-review`
        : `${baseQuoteLink}/${item.component.id}?app=reqs-review`,
      viewerLink: buildPopOutViewerUrl({
        quoteNumberRevisionNumber: item.component.quoteNumberWithRevision,
        quoteItemId: item.component.quoteItemId,
        activePartUuid: item.component.isRootComponent
          ? undefined
          : item.component.part.uuid,
        appsPanelState: 'reqs-review',
      }),
    };
  }, [item]);

  const { generateRelativeDateTime } = useDateFormat();

  const isCurrentGroup = groupId === item.groupId;
  const isUserMultipleGroupMember =
    userGroupMembers &&
    Array.isArray(userGroupMembers) &&
    userGroupMembers.length > 1;

  // Find the business name for this action's group
  type GroupMemberType = {
    id: number;
    group: { id: number; businessName: string };
  };
  const actionGroupMember = Array.isArray(userGroupMembers)
    ? (userGroupMembers as GroupMemberType[]).find(
        (member) => member.group.id === item.groupId,
      )
    : null;
  const businessName = actionGroupMember?.group?.businessName || '';

  const handleClick = () => {
    if (!isCurrentGroup) {
      setIsOpen(true);
    }
  };

  const handleQuoteLinkClick = (e: React.MouseEvent) => {
    if (!isCurrentGroup) {
      e.preventDefault();
      e.stopPropagation();
      setIsQuoteLinkPopoverOpen(true);
    }
  };

  const renderQuoteLink = () => {
    if (isCurrentGroup) {
      return (
        <Button as={Link} variant="link" size="sm" to={quoteLink}>
          Quote #{item.component.quoteNumberWithRevision}
        </Button>
      );
    }

    return (
      <ActionSwitchGroupPopover
        toggleOpen={setIsQuoteLinkPopoverOpen}
        isOpen={isQuoteLinkPopoverOpen}
        nextUrl={quoteLink}
        groupId={item.groupId}
      >
        <Button variant="link" size="sm" onClick={handleQuoteLinkClick}>
          Quote #{item.component.quoteNumberWithRevision}
        </Button>
      </ActionSwitchGroupPopover>
    );
  };

  return isCurrentGroup ? (
    <HStack
      align="flex-start"
      as={Link}
      to={viewerLink}
      color="feature.primary"
      justify="space-between"
      p={1.5}
      backgroundColor="page.surface"
      _hover={{
        color: 'feature.primary',
        backgroundColor: 'interaction.surfaceHover',
      }}
      _active={{
        color: 'feature.primary',
        backgroundColor: 'interaction.surfaceActive',
      }}
      _notLast={{
        borderBottom: '1px solid',
        borderColor: 'separator.divider',
      }}
    >
      <VStack minWidth={0} align="start" gap={0}>
        <Flex gap={0.5} align="center" as="p">
          <Heading display="inline" size="md" as="span">
            {item.rule.name}
          </Heading>{' '}
          {isAssignedByUser && assignedByName ? (
            <Text display="inline" as="span">
              Assigned by {assignedByName}
            </Text>
          ) : (
            <Text display="inline" as="span" color="feature.secondary">
              Assigned automatically
            </Text>
          )}
        </Flex>

        <Flex display="inline-flex" gap={0.5} align="center">
          {item.rule.resolutionTypes.map((resolutionType) => (
            <ResolutionIcon
              key={resolutionType}
              resolutionType={resolutionType}
            />
          ))}

          {item.commentCount > 0 && (
            <Text size="sm" color="feature.primary">
              {pluralize('message', item.commentCount)}
            </Text>
          )}
        </Flex>

        <Flex gap={0.5} align="center">
          {isUserMultipleGroupMember && businessName && (
            <ActionBusinessNameBadge
              businessName={businessName}
              isCurrent={isCurrentGroup}
            />
          )}
          {item.assignedAt && (
            <Text size="sm" color="feature.secondary">
              Assigned {generateRelativeDateTime(item.assignedAt)}
            </Text>
          )}

          {renderQuoteLink()}
        </Flex>
      </VStack>

      <Part
        number={item.component.partNumber}
        filename={item.component.part.filename}
        rev={item.component.revision}
        isItar={item.component.isExportControlled}
        size="lg"
        variant="borderless"
      >
        <PartThumbnailContainer>
          <PartThumbnail
            partFileUuid={item.component.part.primaryFileUuid}
            containsVirus={item.component.part.containsVirus}
            filename={item.component.part.filename}
            fileType={item.component.part.fileType}
            isAssembly={item.component.isAssembly}
            hasFailedChildren={item.component.part.hasFailedChildren}
            processingFailed={item.component.part.processingFailed}
          />
        </PartThumbnailContainer>
      </Part>
    </HStack>
  ) : (
    <ActionSwitchGroupPopover
      toggleOpen={setIsOpen}
      isOpen={isOpen}
      nextUrl={viewerLink}
      groupId={item.groupId}
    >
      <HStack
        as={InteractiveFlex}
        onClick={handleClick}
        cursor="pointer"
        align="flex-start"
        justify="space-between"
        p={1.5}
        _notLast={{
          borderBottom: '1px solid',
          borderColor: 'separator.divider',
        }}
      >
        <VStack minWidth={0} align="start" gap={0}>
          <Flex gap={0.5} align="center" as="p">
            <Heading display="inline" size="md" as="span">
              {item.rule.name}
            </Heading>{' '}
            {isAssignedByUser && assignedByName ? (
              <Text display="inline" as="span" color="feature.secondary">
                Assigned by {assignedByName}
              </Text>
            ) : (
              <Text display="inline" as="span" color="feature.secondary">
                Assigned automatically
              </Text>
            )}
          </Flex>

          <Flex display="inline-flex" gap={0.5} align="center">
            {item.rule.resolutionTypes.map((resolutionType) => (
              <ResolutionIcon
                key={resolutionType}
                resolutionType={resolutionType}
              />
            ))}

            {item.commentCount > 0 && (
              <Text size="sm" color="feature.primary">
                {pluralize('message', item.commentCount)}
              </Text>
            )}
          </Flex>

          <Flex gap={0.5} align="center">
            {isUserMultipleGroupMember && businessName && (
              <ActionBusinessNameBadge
                businessName={businessName}
                isCurrent={isCurrentGroup}
              />
            )}
            {item.assignedAt && (
              <Text size="sm" color="feature.secondary">
                Assigned {generateRelativeDateTime(item.assignedAt)}
              </Text>
            )}

            {renderQuoteLink()}
          </Flex>
        </VStack>

        <Part
          number={item.component.partNumber}
          filename={item.component.part.filename}
          rev={item.component.revision}
          isItar={item.component.isExportControlled}
          size="lg"
          variant="borderless"
        >
          <PartThumbnailContainer>
            <PartThumbnail
              partFileUuid={item.component.part.primaryFileUuid}
              containsVirus={item.component.part.containsVirus}
              filename={item.component.part.filename}
              fileType={item.component.part.fileType}
              isAssembly={item.component.isAssembly}
              hasFailedChildren={item.component.part.hasFailedChildren}
              processingFailed={item.component.part.processingFailed}
            />
          </PartThumbnailContainer>
        </Part>
      </HStack>
    </ActionSwitchGroupPopover>
  );
};
